/**
 * Route Error Boundary
 * Displays error UI for route-level errors using React Router's data router error handling.
 * @packageDocumentation
 */

import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router';

import { Button } from '@/components/ui';

/**
 * Route error boundary component for data router error handling.
 * Renders appropriate error UI based on the error type (404, 403, generic).
 *
 * @returns { JSX.Element } The error boundary element.
 */
export const RouteErrorBoundary = () => {
  const { t } = useTranslation();
  const error = useRouteError();
  const navigate = useNavigate();

  let title = t('error.somethingWentWrong');
  let message = t('error.unexpectedRetry');
  let status: number | undefined;

  if (isRouteErrorResponse(error)) {
    status = error.status;

    if (error.status === 404) {
      title = t('error.pageNotFound');
      message = t('error.pageNotFoundMessage');
    } else if (error.status === 403) {
      title = t('error.accessDenied');
      message = t('error.accessDeniedMessage');
    } else {
      message = error.statusText || message;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8">
      <div className="text-center">
        {status && <p className="text-5xl font-bold text-muted-foreground/50">{status}</p>}
        <h1 className="mt-2 text-2xl font-semibold text-foreground">{title}</h1>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">{message}</p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t('actions.goBack')}
        </Button>
        <Button onClick={() => navigate('/dashboard')}>{t('actions.goToSummary')}</Button>
      </div>
    </div>
  );
};
